/**
 * FullscreenOverlayBaseHeader - Header component for fullscreen overlays
 *
 * Builds a badge row from structured props (typeBadge, filePath, title, subtitle).
 * File actions are visible in the header and also available from the path
 * badge's context menu.
 * onOpenFileExternal and onRevealInFinder come from PlatformContext — no per-overlay callbacks.
 */

import { useState, useCallback, type ReactNode } from 'react'
import * as ContextMenu from '@radix-ui/react-context-menu'
import { Check, Copy, Download, ExternalLink, FolderOpen, type LucideIcon } from 'lucide-react'
import { PreviewHeader, PreviewHeaderBadge, PREVIEW_HEADER_CONTROL_CLASSES, PREVIEW_HEADER_CONTROL_INTERACTION_CLASSES, type PreviewBadgeVariant } from '../ui/PreviewHeader'
import { usePlatform } from '../../context/PlatformContext'
import { useTranslation } from '@okclawpro/i18n/react'
import { cn } from '../../lib/utils'
import { isDirectImageSource } from '../../lib/image-preview'

/** Structured type badge — tool/format indicator (e.g. "Read", "Image", "Bash") */
export interface OverlayTypeBadge {
  icon: LucideIcon
  label: string
  variant?: PreviewBadgeVariant
}

export interface FullscreenOverlayBaseHeaderProps {
  /** Close handler — shows X button in header */
  onClose: () => void
  /** Type badge — tool/format indicator */
  typeBadge?: OverlayTypeBadge
  /** File path — shows a path badge and visible platform file actions */
  filePath?: string
  /** Title — displayed as a badge. Fallback when no file path. */
  title?: string
  /** Click handler for the title badge */
  onTitleClick?: () => void
  /** Subtitle — extra info badge (e.g. "Lines 1-50 of 200") */
  subtitle?: string
  /** Right-side actions (e.g. diff controls) */
  headerActions?: ReactNode
  /** When provided, renders a built-in copy button (matching close button style) */
  copyContent?: string
}

/**
 * Truncates a file path to show just the filename for display in the badge.
 * Full path is available via tooltip.
 */
function displayPath(filePath: string): string {
  const parts = filePath.split('/')
  const name = parts.pop() || filePath
  // Show parent dir + filename if available (e.g. "src/App.tsx")
  if (parts.length > 0) {
    const parent = parts.pop()
    return `${parent}/${name}`
  }
  return name
}

// ============================================================================
// Shared context menu styling — matches StyledDropdown's popover-styled look
// ============================================================================

const contextMenuContentClasses = cn(
  'popover-styled z-dropdown min-w-40 overflow-hidden p-1',
  'w-fit font-sans whitespace-nowrap text-xs flex flex-col gap-0.5',
  'animate-in fade-in-0 zoom-in-95'
)

const contextMenuItemClasses = cn(
  'relative flex cursor-default items-center gap-2 px-2 py-1.5 text-sm outline-hidden select-none',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  'pr-4 rounded-[4px] hover:bg-foreground/[0.03] focus:bg-foreground/[0.03]',
  '[&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0'
)

// ============================================================================
// FilePathBadge — path badge with a secondary context menu
// ============================================================================

interface FilePathBadgeProps {
  filePath: string
}

/**
 * FilePathBadge - File name badge with a right-click menu for power users.
 *
 * The primary file actions are intentionally rendered as visible header buttons
 * next to the badge. The context menu remains as a compact fallback for users
 * who prefer a contextual interaction.
 * Uses onOpenFileExternal (not onOpenFile) from PlatformContext — when already
 * viewing a file in an overlay, "Open" should launch the system editor directly,
 * not re-trigger the in-app preview interceptor.
 */
function FilePathBadge({ filePath }: FilePathBadgeProps) {
  const { t } = useTranslation()
  const { onOpenFileExternal, onRevealInFinder, fileManagerName, openFileExternalLabel } = usePlatform()
  const revealLabel = t('overlay.revealInFinder', { fileManager: localizedFileManagerName(fileManagerName, t) })
  const openLabel = openFileExternalLabel || t('overlay.openFile')
  const OpenIcon = openFileExternalLabel ? Download : ExternalLink

  const handleOpen = useCallback(() => {
    onOpenFileExternal?.(filePath)
  }, [onOpenFileExternal, filePath])

  const handleReveal = useCallback(() => {
    onRevealInFinder?.(filePath)
  }, [onRevealInFinder, filePath])

  const hasMenuItems = !!onOpenFileExternal || !!onRevealInFinder

  const contextItems = (
    <>
      {onOpenFileExternal && (
        <ContextMenu.Item className={contextMenuItemClasses} onSelect={handleOpen}>
          <OpenIcon />
          {openLabel}
        </ContextMenu.Item>
      )}
      {onRevealInFinder && (
        <ContextMenu.Item className={contextMenuItemClasses} onSelect={handleReveal}>
          <FolderOpen />
          {revealLabel}
        </ContextMenu.Item>
      )}
    </>
  )

  const display = displayPath(filePath)

  // If no menu items available (e.g. web viewer), just show a static badge
  if (!hasMenuItems) {
    return (
        <PreviewHeaderBadge
          label={display}
          title={filePath}
          shrinkable
          asButton
          className="preview-file-path"
        style={{ flex: '1 1 0', minWidth: 0, width: 0, maxWidth: 'min(48cqw, 40rem)' }}
      />
    )
  }

  // Keep the context menu as a secondary interaction. The primary actions are
  // rendered next to the badge by FullscreenOverlayBaseHeader.
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <PreviewHeaderBadge
          label={display}
          title={filePath}
          shrinkable
          asButton
          className="preview-file-path cursor-default"
          style={{ flex: '1 1 0', minWidth: 0, width: 0, maxWidth: 'min(48cqw, 40rem)' }}
        />
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className={contextMenuContentClasses}>
          {contextItems}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  )
}

interface FileActionButtonProps {
  icon: LucideIcon
  label: string
  onClick: () => void
}

/**
 * A visible action in the preview toolbar. Labels are kept at desktop widths
 * and collapse to icon-only controls on narrow embedded previews.
 */
function FileActionButton({ icon: Icon, label, onClick }: FileActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'preview-file-action shrink-0 cursor-pointer',
        PREVIEW_HEADER_CONTROL_CLASSES,
        PREVIEW_HEADER_CONTROL_INTERACTION_CLASSES
      )}
      title={label}
      aria-label={label}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="preview-file-action-label">{label}</span>
    </button>
  )
}

function localizedFileManagerName(fileManagerName: string | undefined, t: ReturnType<typeof useTranslation>['t']): string {
  switch (fileManagerName) {
    case 'Finder':
      return t('overlay.fileManagerFinder')
    case 'Explorer':
      return t('overlay.fileManagerExplorer')
    case 'File Manager':
    case undefined:
      return t('overlay.fileManagerGeneric')
    default:
      return fileManagerName
  }
}

// ============================================================================
// FullscreenOverlayBaseHeader
// ============================================================================

export function FullscreenOverlayBaseHeader({
  onClose,
  typeBadge,
  filePath,
  title,
  onTitleClick,
  subtitle,
  headerActions,
  copyContent,
}: FullscreenOverlayBaseHeaderProps) {
  const { t } = useTranslation()
  const { onOpenFileExternal, onRevealInFinder, fileManagerName, openFileExternalLabel } = usePlatform()
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    if (!copyContent) return
    try {
      await navigator.clipboard.writeText(copyContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [copyContent])

  const shouldRenderFileActions = Boolean(filePath && !isDirectImageSource(filePath))
  const localizedManagerName = localizedFileManagerName(fileManagerName, t)
  const openFileLabel = openFileExternalLabel || t('overlay.openFile')
  const OpenFileIcon = openFileExternalLabel ? Download : ExternalLink
  const revealFileLabel = t('overlay.revealInFinder', { fileManager: localizedManagerName })

  const fileActions = shouldRenderFileActions ? (
    <>
      {onOpenFileExternal && (
        <FileActionButton icon={OpenFileIcon} label={openFileLabel} onClick={() => onOpenFileExternal(filePath!)} />
      )}
      {onRevealInFinder && (
        <FileActionButton icon={FolderOpen} label={revealFileLabel} onClick={() => onRevealInFinder(filePath!)} />
      )}
    </>
  ) : null

  // Built-in file/copy buttons + any custom header actions, rendered in
  // PreviewHeader's right actions area so they remain immediately discoverable.
  const rightActions = (
    <>
      {fileActions}
      {copyContent != null && (
        <button
          onClick={handleCopy}
          className={cn(
            'p-1.5 rounded-[6px] bg-background shadow-minimal cursor-pointer',
            'opacity-70 hover:opacity-100 transition-opacity',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'
          )}
          title={copied ? t('ui.copied') : t('overlay.copyAll')}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      )}
      {headerActions}
    </>
  )

  const shouldRenderFileMenu = filePath ? !isDirectImageSource(filePath) : false
  const fileBadge = filePath ? (
    shouldRenderFileMenu ? (
      <FilePathBadge filePath={filePath} />
    ) : (
      <PreviewHeaderBadge
        label={displayPath(filePath)}
        title={filePath}
        shrinkable
        className="preview-file-path"
        style={{ flex: '1 1 0', minWidth: 0, width: 0, maxWidth: 'min(48cqw, 40rem)' }}
      />
    )
  ) : null

  return (
    <PreviewHeader
      onClose={onClose}
      height={48}
      rightActions={rightActions}
      style={{ containerName: 'previewHeader', containerType: 'inline-size' }}
    >
      {typeBadge && (
        <PreviewHeaderBadge
          icon={typeBadge.icon}
          label={typeBadge.label}
          variant={typeBadge.variant}
          asButton
        />
      )}
      {subtitle && <PreviewHeaderBadge label={subtitle} asButton />}
      {fileBadge ?? (title ? (
        <PreviewHeaderBadge label={title} onClick={onTitleClick} shrinkable asButton />
      ) : null)}
    </PreviewHeader>
  )
}
